// src/pages/ContactsListPage.tsx
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contactsApi, accountsApi } from '../api';
import { 
  Table, TableBody, TableCell, TableHead, TableRow, Typography, 
  Paper, CircularProgress, Box, Button, TextField, Grid, 
  Dialog, DialogTitle, DialogContent, DialogActions, Link,
  Chip, Avatar, IconButton, Tooltip, FormControl, InputLabel,
  Select, MenuItem, Pagination, Alert
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { Add as AddIcon, Search as SearchIcon, Email as EmailIcon, Phone as PhoneIcon,
         Person as PersonIcon, Business as BusinessIcon } from '@mui/icons-material';
import type { ContactReadWithAccount, ContactCreate, Account } from '../types/api';

interface ContactFormData {
  email: string;
  first_name: string;
  last_name: string;
  title: string;
  phone: string;
  notes: string;
  account_id: string;
}

interface ContactFilters {
  search: string;
  account_id: string;
  page: number;
  limit: number;
}

const ITEMS_PER_PAGE = 25;

// Helper function to get contact display name
const getContactDisplayName = (contact: ContactReadWithAccount): string => {
  const firstName = contact.first_name || '';
  const lastName = contact.last_name || '';
  if (firstName || lastName) {
    return `${firstName} ${lastName}`.trim();
  }
  return contact.email;
};

// Helper function to get contact initials for avatar
const getContactInitials = (contact: ContactReadWithAccount): string => {
  const firstName = contact.first_name || '';
  const lastName = contact.last_name || '';
  if (firstName || lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  return contact.email.charAt(0).toUpperCase();
};

export function ContactsListPage() {
  const [errorDetails, setErrorDetails] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    email: '',
    first_name: '',
    last_name: '',
    title: '',
    phone: '',
    notes: '',
    account_id: ''
  });
  const [formError, setFormError] = useState('');
  const [filters, setFilters] = useState<ContactFilters>({
    search: '',
    account_id: '',
    page: 1,
    limit: ITEMS_PER_PAGE
  });
  
  const queryClient = useQueryClient();

  // Fetch contacts with filtering
  const { data: contacts, isLoading, isError, error, refetch } = useQuery<ContactReadWithAccount[]>({ 
    queryKey: ['contacts', filters], 
    queryFn: async () => {
      try {
        console.log('Fetching contacts with filters:', filters);
        const apiFilters: any = {
          skip: (filters.page - 1) * filters.limit,
          limit: filters.limit
        };
        
        if (filters.search) {
          apiFilters.q = filters.search;
        }
        
        if (filters.account_id) {
          apiFilters.account_id = filters.account_id;
        }
        
        return await contactsApi.getContacts(apiFilters);
      } catch (error) {
        console.error('Error fetching contacts:', error);
        throw error;
      }
    },
    retry: 1,
    onError: (err: any) => {
      console.error('Query error:', err);
      setErrorDetails(
        `Status: ${err?.response?.status || 'Unknown'}\n` +
        `Message: ${err?.message || 'Unknown error'}\n` +
        `Response: ${JSON.stringify(err?.response?.data || {}, null, 2)}`
      );
    }
  });

  // Fetch accounts for the dropdown
  const { data: accounts = [] } = useQuery<Account[]>({
    queryKey: ['accounts'],
    queryFn: async () => {
      try {
        return await accountsApi.getAccounts();
      } catch (error) {
        console.error('Error fetching accounts:', error);
        return [];
      }
    }
  });

  // Create contact mutation
  const createContactMutation = useMutation({
    mutationFn: async (contactData: ContactCreate) => {
      return await contactsApi.createContact(contactData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setDialogOpen(false);
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        title: '',
        phone: '',
        notes: '',
        account_id: ''
      });
      setFormError('');
    },
    onError: (error: any) => {
      console.error('Error creating contact:', error);
      setFormError(error?.response?.data?.detail || 'Failed to create contact');
    }
  });

  // Filter contacts based on search and account
  const filteredContacts = useMemo(() => {
    if (!contacts) return [];
    return contacts;
  }, [contacts]);

  // Handle form submission
  const handleSubmit = () => {
    if (!formData.email || !formData.account_id) {
      setFormError('Email and Account are required');
      return;
    }

    const contactData: ContactCreate = {
      email: formData.email.trim(),
      first_name: formData.first_name.trim() || undefined,
      last_name: formData.last_name.trim() || undefined,
      title: formData.title.trim() || undefined,
      phone: formData.phone.trim() || undefined,
      notes: formData.notes.trim() || undefined,
      account_id: formData.account_id
    };

    createContactMutation.mutate(contactData);
  };

  // Handle search
  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  };

  // Handle account filter
  const handleAccountFilterChange = (accountId: string) => {
    setFilters(prev => ({ ...prev, account_id: accountId, page: 1 }));
  };

  // Handle pagination
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setFilters(prev => ({ ...prev, page: value }));
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load contacts. Please try again.
        </Alert>
        <Button onClick={() => refetch()} variant="contained">
          Retry
        </Button>
        {errorDetails && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
              {errorDetails}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Contacts
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Add Contact
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search contacts"
              placeholder="Search by name, email, or title"
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Filter by Account</InputLabel>
              <Select
                value={filters.account_id}
                label="Filter by Account"
                onChange={(e) => handleAccountFilterChange(e.target.value)}
              >
                <MenuItem value="">All Accounts</MenuItem>
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              {filteredContacts.length} contact{filteredContacts.length !== 1 ? 's' : ''} found
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Contacts Table */}
      <Paper data-testid="contacts" className="contacts-list">
        <Table data-testid="contacts-table">
          <TableHead>
            <TableRow>
              <TableCell>Contact</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredContacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {filters.search || filters.account_id ? 'No contacts match your filters' : 'No contacts found'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {!filters.search && !filters.account_id && 'Create your first contact to get started'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredContacts.map((contact) => (
                <TableRow key={contact.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {getContactInitials(contact)}
                      </Avatar>
                      <Box>
                        <Link
                          component={RouterLink}
                          to={`/contacts/${contact.id}`}
                          sx={{ textDecoration: 'none', fontWeight: 'medium' }}
                        >
                          {getContactDisplayName(contact)}
                        </Link>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2">{contact.email}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {contact.phone ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2">{contact.phone}</Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {contact.title ? (
                      <Chip
                        label={contact.title}
                        size="small"
                        variant="outlined"
                        icon={<PersonIcon />}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon fontSize="small" color="action" />
                      {contact.account ? (
                        <Link
                          component={RouterLink}
                          to={`/accounts/${contact.account.id}`}
                          sx={{ textDecoration: 'none' }}
                        >
                          {contact.account.name}
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No Account
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(contact.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View contact details">
                      <IconButton
                        component={RouterLink}
                        to={`/contacts/${contact.id}`}
                        size="small"
                      >
                        <PersonIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Pagination */}
      {filteredContacts.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(filteredContacts.length / ITEMS_PER_PAGE)}
            page={filters.page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Create Contact Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Contact</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email *"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                error={!formData.email && formError.includes('Email')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!formData.account_id && formError.includes('Account')}>
                <InputLabel>Account *</InputLabel>
                <Select
                  value={formData.account_id}
                  label="Account *"
                  onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                >
                  {accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createContactMutation.isLoading}
          >
            {createContactMutation.isLoading ? 'Creating...' : 'Create Contact'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
